import { Model } from '@contember/schema';
import { noCase } from 'change-case';
import { formatEntityName, formatFieldName, formatRelationType } from '../../../common';
import { createSchemaStatementHandler } from '../../createSchemaStatementHandler';
import { SchemaDslBuilderError } from '../../SchemaDslBuilder';
export const addRelationStmtInstruction = `
#### Relationship Definition
- Syntax: \`add relation <owning_entity>.<owning_field> <relationship_type> <inverse_entity>.<inverse_field> [required];\`
- nullable flag make sense only for one to one and many to one relations
- Example: \`add relation Post.author many to one Author.posts;\`
- Example: \`add relation Country.capitalCity one to one City.capitalOf required;\`
- Example: \`add relation Post.tags many to many Tag.posts;\``;
export const stmtAddRelation = ({ owningEntity, owningField, relationshipType, inverseEntity, inverseField, nullable = true, }) => {
    return `add relation ${owningEntity}.${owningField} ${relationshipType} ${inverseEntity}${inverseField ? '.' + inverseField : ''}${nullable ? '' : ' required'};`;
};
export const stmtForHumanAddRelation = ({ owningEntity, owningField, relationshipType, inverseEntity, inverseField, nullable = true, }) => {
    return (`Add a new relationship between the database tables __${noCase(owningEntity)}__ and __${noCase(inverseEntity)}__. The relationship is of type __${noCase(relationshipType)}__ and is named __${noCase(owningField)}__ on the __${noCase(owningEntity)}__ side and __${inverseField ? noCase(inverseField) : noCase(owningField)}__ on the __${noCase(inverseEntity)}__ side.` + (nullable ? '' : ' The relationship is required.'));
};
export const relationTypeMap = {
    [Model.RelationType.OneHasOne]: 'one to one',
    [Model.RelationType.ManyHasOne]: 'many to one',
    [Model.RelationType.OneHasMany]: 'one to many',
    [Model.RelationType.ManyHasMany]: 'many to many',
};
const isRelationType = (value) => value ? ['one to one', 'many to one', 'many to many', 'one to many'].includes(value) : false;
export const addRelationHandler = createSchemaStatementHandler({
    type: 'add relation',
    parse: line => {
        const match = /^add relation (?<owningEntityName>\w+)\.(?<owningFieldName>\w+) (?<relationshipType>\w+ to \w+) (?<inverseEntityName>\w+)(\.(?<inverseFieldName>\w+))?(?<required> required)?/.exec(line);
        const relationshipType = formatRelationType(match?.groups?.relationshipType ?? '');
        if (relationshipType && !isRelationType(relationshipType)) {
            throw new SchemaDslBuilderError(`Unknown relationship type ${relationshipType}`);
        }
        return match?.groups
            ? {
                type: 'add relation',
                owningEntity: formatEntityName(match.groups.owningEntityName),
                owningField: formatFieldName(match.groups.owningFieldName),
                relationshipType: relationshipType,
                inverseEntity: formatEntityName(match.groups.inverseEntityName),
                inverseField: match.groups.inverseFieldName ? formatFieldName(match.groups.inverseFieldName) : undefined,
                nullable: !match.groups.required,
            }
            : null;
    },
    getHumanStmt: stmt => stmtForHumanAddRelation(stmt),
    apply: (stmt, builder) => {
        builder.updateSchema(it => {
            const { owningEntity, owningField, relationshipType, inverseEntity, inverseField, nullable } = stmt;
            if (it.schema.model.entities[owningEntity]?.fields[owningField]) {
                it = it.removeField(owningEntity, owningField);
            }
            switch (relationshipType) {
                case 'one to one':
                    if (inverseField) {
                        return it
                            .addRelation(owningEntity, {
                            type: Model.RelationType.OneHasOne,
                            name: owningField,
                            target: inverseEntity,
                            inversedBy: inverseField,
                            nullable: !!nullable,
                        })
                            .addRelation(inverseEntity, {
                            type: Model.RelationType.OneHasOne,
                            name: inverseField,
                            target: owningEntity,
                            ownedBy: owningField,
                        });
                    }
                    return it.addRelation(owningEntity, {
                        type: Model.RelationType.OneHasOne,
                        name: owningField,
                        target: inverseEntity,
                        nullable: !!nullable,
                    });
                case 'many to one':
                    if (inverseField) {
                        return it
                            .addRelation(owningEntity, {
                            type: Model.RelationType.ManyHasOne,
                            name: owningField,
                            target: inverseEntity,
                            inversedBy: inverseField,
                            nullable: !!nullable,
                        })
                            .addRelation(inverseEntity, {
                            type: Model.RelationType.OneHasMany,
                            name: inverseField,
                            target: owningEntity,
                            ownedBy: owningField,
                        });
                    }
                    return it.addRelation(owningEntity, {
                        type: Model.RelationType.ManyHasOne,
                        name: owningField,
                        target: inverseEntity,
                        nullable: !!nullable,
                    });
                case 'many to many':
                    if (inverseField) {
                        return it
                            .addRelation(owningEntity, {
                            type: Model.RelationType.ManyHasMany,
                            name: owningField,
                            target: inverseEntity,
                            inversedBy: inverseField,
                        })
                            .addRelation(inverseEntity, {
                            type: Model.RelationType.ManyHasMany,
                            name: inverseField,
                            target: owningEntity,
                            ownedBy: owningField,
                        });
                    }
                    return it.addRelation(owningEntity, {
                        type: Model.RelationType.ManyHasMany,
                        name: owningField,
                        target: inverseEntity,
                        inversedBy: inverseField,
                    });
                case 'one to many':
                    if (!inverseField) {
                        throw new SchemaDslBuilderError('Inverse field is required for one to many relationship');
                    }
                    return it
                        .addRelation(owningEntity, {
                        type: Model.RelationType.OneHasMany,
                        name: owningField,
                        target: inverseEntity,
                        ownedBy: inverseField,
                    })
                        .addRelation(inverseEntity, {
                        type: Model.RelationType.ManyHasOne,
                        name: inverseField,
                        target: owningEntity,
                        inversedBy: owningField,
                        nullable: !!nullable,
                    });
                default:
                    throw new SchemaDslBuilderError(`Unknown relationship type ${relationshipType}`);
            }
        });
    },
});
//# sourceMappingURL=AddRelationStmt.js.map